import { useState, useEffect, useCallback } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import type { User } from '../types'

interface UseUsersOptions {
  page?: number
  limit?: number
  search?: string
  tier?: 'free' | 'premium' | 'beta' | 'all'
}

export function useUsers(client: SupabaseClient | null, options: UseUsersOptions = {}) {
  const { page = 1, limit = 20, search = '', tier = 'all' } = options

  const [users, setUsers] = useState<User[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    if (!client) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let query = client
        .from('users')
        .select('*', { count: 'exact' })

      // Apply search filter
      if (search) {
        query = query.or(`email.ilike.%${search}%,first_name.ilike.%${search}%,last_name.ilike.%${search}%`)
      }

      // Apply tier filter
      if (tier !== 'all') {
        query = query.eq('subscription_tier', tier)
      }

      // Apply pagination
      const from = (page - 1) * limit
      const to = from + limit - 1
      query = query.range(from, to).order('created_at', { ascending: false })

      const { data, error: queryError, count } = await query

      if (queryError) throw queryError

      setUsers(data || [])
      setTotal(count || 0)
    } catch (err: any) {
      console.error('Error fetching users:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [client, page, limit, search, tier])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  return { users, total, isLoading, error, refetch: fetchUsers }
}

export function useUserDetail(client: SupabaseClient | null, userId: string | null) {
  const [user, setUser] = useState<User | null>(null)
  const [recentScans, setRecentScans] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!client || !userId) {
      setIsLoading(false)
      return
    }

    const supabaseClient = client

    async function fetchUserDetail() {
      setIsLoading(true)
      setError(null)

      try {
        // Fetch user
        const { data: userData, error: userError } = await supabaseClient
          .from('users')
          .select('*')
          .eq('id', userId)
          .single()

        if (userError) throw userError
        setUser(userData)

        // Fetch recent scans
        const { data: scansData } = await supabaseClient
          .from('recent_scans')
          .select('*')
          .eq('user_id', userId)
          .order('scanned_at', { ascending: false })
          .limit(10)

        setRecentScans(scansData || [])
      } catch (err: any) {
        console.error('Error fetching user detail:', err)
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchUserDetail()
  }, [client, userId])

  return { user, recentScans, isLoading, error }
}
